import {Injectable} from '@angular/core';
import {cloneDeep, isEqual} from 'lodash';
import {DateTime} from 'luxon';
import {JackingFormInstanceEditor, JackingFieldTemplateEditor} from '../../models/jacking-v2/jacking.models';

export type JackingMergeChoice = 'mine' | 'theirs';

export interface JackingMergeConflict {
  id: string;
  path: Array<string | number>;
  label: string;
  mine: any;
  theirs: any;
  mineDisplay: string;
  theirsDisplay: string;
}

export interface JackingMergeIncomingChange {
  id: string;
  path: Array<string | number>;
  label: string;
  previous: any;
  theirs: any;
  previousDisplay: string;
  theirsDisplay: string;
}

export interface JackingMergeResult {
  mergedEditor: JackingFormInstanceEditor;
  conflicts: JackingMergeConflict[];
  incomingChanges: JackingMergeIncomingChange[];
}

interface MergeContext {
  conflicts: JackingMergeConflict[];
  incomingChanges: JackingMergeIncomingChange[];
}

const ROOT_SERVER_PROPERTIES = new Set(['revision', 'statusId', 'template', 'optionsByKey', 'dataByKey']);
const UI_ONLY_PROPERTIES = new Set([
  'customMissingProviders', '_missingProvidersRegistered', 'timeToShow', 'selectedLocalDateIso', '_mask', '_isReadonly'
]);
const IDENTITY_PROPERTIES = new Set(['id', 'fieldTemplateId', 'eventRowTemplateId', 'legNumber']);

/** Pure, reusable three-way merge for Jacking editors. None of the input editors are mutated. */
@Injectable()
export class JackingThreeWayMergeService {
  synchronizeRepresentations(editor: JackingFormInstanceEditor): JackingFormInstanceEditor {
    const synchronized = cloneDeep(editor);
    synchronized.fieldValues = this.syncFieldValues(synchronized.fieldValues, synchronized.valuesByFieldId);
    return synchronized;
  }

  merge(
    baseline: JackingFormInstanceEditor,
    local: JackingFormInstanceEditor,
    server: JackingFormInstanceEditor
  ): JackingMergeResult {
    const context: MergeContext = {conflicts: [], incomingChanges: []};
    const merged = this.mergeNode(baseline, local, server, [], context) as JackingFormInstanceEditor;

    // valuesByFieldId is the representation bound by the V2 controls. fieldValues is an API
    // representation of the same inputs and is deliberately kept in step without comparing it twice.
    merged.fieldValues = this.syncFieldValues(server.fieldValues, merged.valuesByFieldId);
    merged.revision = server.revision;

    context.conflicts.forEach(conflict => {
      conflict.label = this.resolveLabel(conflict.path, merged);
      conflict.mineDisplay = this.formatConflictValue(conflict.mine, conflict.path, merged);
      conflict.theirsDisplay = this.formatConflictValue(conflict.theirs, conflict.path, merged);
    });

    context.incomingChanges = context.incomingChanges.filter(change =>
      !this.isInitializationOnlyIncomingChange(change, merged)
    );
    context.incomingChanges.forEach(change => {
      change.label = this.resolveLabel(change.path, merged);
      change.previousDisplay = this.formatConflictValue(change.previous, change.path, merged);
      change.theirsDisplay = this.formatConflictValue(change.theirs, change.path, merged);
    });

    return {
      mergedEditor: merged,
      conflicts: context.conflicts,
      incomingChanges: context.incomingChanges
    };
  }

  applyChoices(result: JackingMergeResult, choices: {[conflictId: string]: JackingMergeChoice}): JackingFormInstanceEditor {
    const editor = cloneDeep(result.mergedEditor);

    for (const conflict of result.conflicts) {
      const value = choices[conflict.id] === 'theirs' ? conflict.theirs : conflict.mine;
      this.setAtPath(editor, conflict.path, cloneDeep(value));
    }

    editor.fieldValues = this.syncFieldValues(editor.fieldValues, editor.valuesByFieldId);
    return editor;
  }

  private mergeNode(base: any, local: any, server: any, path: Array<string | number>, context: MergeContext): any {
    const property = path.length ? String(path[path.length - 1]) : '';

    if (path.length === 1 && ROOT_SERVER_PROPERTIES.has(property)) {
      return cloneDeep(server);
    }
    if (path.length === 1 && property === 'fieldValues') {
      return cloneDeep(server);
    }
    if (UI_ONLY_PROPERTIES.has(property) || property.charAt(0) === '_') {
      return cloneDeep(local !== undefined ? local : server);
    }

    if (this.valuesEqual(local, server, path)) {
      return cloneDeep(server);
    }
    if (this.valuesEqual(local, base, path)) {
      this.collectIncomingChanges(base, server, path, context);
      return cloneDeep(server);
    }
    if (this.valuesEqual(server, base, path)) {
      return cloneDeep(local);
    }

    // A field value is one logical input even when it contains a date, offset and confirmation flag.
    if (path.length === 2 && path[0] === 'valuesByFieldId') {
      return this.addConflict(local, server, path, context);
    }

    if (this.isDateValue(base) || this.isDateValue(local) || this.isDateValue(server)) {
      return this.addConflict(local, server, path, context);
    }

    if (Array.isArray(base) || Array.isArray(local) || Array.isArray(server)) {
      return this.mergeArray(base, local, server, path, context);
    }

    if (this.isObject(base) || this.isObject(local) || this.isObject(server)) {
      return this.mergeObject(base, local, server, path, context);
    }

    return this.addConflict(local, server, path, context);
  }

  private mergeObject(base: any, local: any, server: any, path: Array<string | number>, context: MergeContext): any {
    if ((base != null && !this.isObject(base)) || (local != null && !this.isObject(local)) ||
        (server != null && !this.isObject(server))) {
      return this.addConflict(local, server, path, context);
    }

    const result: any = {};
    const keys = this.uniqueKeys(base, local, server);
    keys.forEach(key => {
      const merged = this.mergeNode(base && base[key], local && local[key], server && server[key], path.concat(key), context);
      if (merged !== undefined) {
        result[key] = merged;
      }
    });
    return result;
  }

  private mergeArray(baseValue: any, localValue: any, serverValue: any, path: Array<string | number>, context: MergeContext): any[] {
    const base = Array.isArray(baseValue) ? baseValue : [];
    const local = Array.isArray(localValue) ? localValue : [];
    const server = Array.isArray(serverValue) ? serverValue : [];

    if (!this.hasStableArrayIdentity(base, local, server, path)) {
      return this.addConflict(local, server, path, context);
    }

    const baseMap = this.toIdentityMap(base, path);
    const localMap = this.toIdentityMap(local, path);
    const serverMap = this.toIdentityMap(server, path);
    const identities: string[] = [];
    [...server, ...local, ...base].forEach(item => {
      const identity = this.arrayIdentity(item, path);
      if (identity != null && identities.indexOf(identity) < 0) {
        identities.push(identity);
      }
    });

    const result: any[] = [];
    identities.forEach(identity => {
      const outputIndex = result.length;
      const merged = this.mergeNode(
        baseMap[identity], localMap[identity], serverMap[identity], path.concat(outputIndex), context
      );
      if (merged !== undefined && merged !== null) {
        result.push(merged);
      }
    });

    return result;
  }

  private addConflict(local: any, server: any, path: Array<string | number>, context: MergeContext): any {
    const id = path.map(String).join('.');
    context.conflicts.push({
      id,
      path: path.slice(),
      label: '',
      mine: cloneDeep(local),
      theirs: cloneDeep(server),
      mineDisplay: '',
      theirsDisplay: ''
    });
    return cloneDeep(local);
  }

  private collectIncomingChanges(
    base: any,
    server: any,
    path: Array<string | number>,
    context: MergeContext
  ): void {
    if (this.valuesEqual(base, server, path)) {
      return;
    }

    const property = path.length ? String(path[path.length - 1]) : '';
    if (path.length === 1 && (ROOT_SERVER_PROPERTIES.has(property) || property === 'fieldValues')) {
      return;
    }
    if (UI_ONLY_PROPERTIES.has(property) || property.charAt(0) === '_' || IDENTITY_PROPERTIES.has(property)) {
      return;
    }

    // One field value and one date/time control are each presented as a single user-facing change.
    if ((path.length === 2 && path[0] === 'valuesByFieldId') ||
        this.isDateValue(base) || this.isDateValue(server) ||
        this.isSerializedLuxonDateTime(base, path) || this.isSerializedLuxonDateTime(server, path)) {
      this.addIncomingChange(base, server, path, context);
      return;
    }

    if (Array.isArray(base) || Array.isArray(server)) {
      const baseItems = Array.isArray(base) ? base : [];
      const serverItems = Array.isArray(server) ? server : [];
      if (!this.hasStableArrayIdentity(baseItems, baseItems, serverItems, path)) {
        this.addIncomingChange(base, server, path, context);
        return;
      }

      const baseMap = this.toIdentityMap(baseItems, path);
      const serverMap = this.toIdentityMap(serverItems, path);
      const identities: string[] = [];
      [...serverItems, ...baseItems].forEach(item => {
        const identity = this.arrayIdentity(item, path);
        if (identity != null && identities.indexOf(identity) < 0) {
          identities.push(identity);
        }
      });
      identities.forEach((identity, index) => {
        this.collectIncomingChanges(baseMap[identity], serverMap[identity], path.concat(index), context);
      });
      return;
    }

    if (this.isObject(base) || this.isObject(server)) {
      if ((base != null && !this.isObject(base)) || (server != null && !this.isObject(server))) {
        this.addIncomingChange(base, server, path, context);
        return;
      }
      this.uniqueKeys(base, server).forEach(key => {
        this.collectIncomingChanges(base && base[key], server && server[key], path.concat(key), context);
      });
      return;
    }

    this.addIncomingChange(base, server, path, context);
  }

  private addIncomingChange(
    base: any,
    server: any,
    path: Array<string | number>,
    context: MergeContext
  ): void {
    const id = path.map(String).join('.');
    if (context.incomingChanges.some(change => change.id === id)) {
      return;
    }
    context.incomingChanges.push({
      id,
      path: path.slice(),
      label: '',
      previous: cloneDeep(base),
      theirs: cloneDeep(server),
      previousDisplay: '',
      theirsDisplay: ''
    });
  }

  private valuesEqual(left: any, right: any, path: Array<string | number>): boolean {
    return isEqual(this.normalize(left, path), this.normalize(right, path));
  }

  private normalize(value: any, path: Array<string | number>): any {
    if (value === null || value === undefined) {
      return null;
    }

    if (this.isPtLuxonDateWrapper(value)) {
      return this.normalizeDateTime(value.dateTime);
    }

    if (this.isSerializedLuxonDateTime(value, path)) {
      return DateTime.fromMillis(Number(value.ts)).toUTC().toISO();
    }

    if (value === false && this.isConfirmationPath(path)) {
      return null;
    }

    if (this.isEmptyFieldValueShell(value) || this.isEmptyPlaceholderShell(value, path) ||
        this.isEmptyLiftPumpsShell(value, path)) {
      return null;
    }

    if (this.isLuxonDateTime(value)) {
      return value.toUTC().toISO();
    }

    if (Array.isArray(value)) {
      const normalized = value
        .map((item, index) => this.normalize(item, path.concat(index)))
        .filter(item => item !== null);
      if (value.every(item => item == null || typeof item !== 'object')) {
        const sorted = normalized.slice().sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
        return sorted.length ? sorted : null;
      }
      if (this.hasStableArrayIdentity(value, value, value, path)) {
        const sorted = normalized.slice().sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
        return sorted.length ? sorted : null;
      }
      return normalized.length ? normalized : null;
    }

    if (this.isObject(value)) {
      const normalized: any = {};
      Object.keys(value).sort().forEach(key => {
        if (UI_ONLY_PROPERTIES.has(key) || key.charAt(0) === '_' ||
            (path.length === 0 && (ROOT_SERVER_PROPERTIES.has(key) || key === 'fieldValues'))) {
          return;
        }
        const normalizedValue = this.normalize(value[key], path.concat(key));
        // A control may create a minimal object when first rendered while the API returns
        // the same object with every empty property explicitly set to null.
        if (normalizedValue !== null) {
          normalized[key] = normalizedValue;
        }
      });
      return Object.keys(normalized).length ? normalized : null;
    }

    if (typeof value === 'string' && this.isDatePath(path)) {
      const parsed = DateTime.fromISO(value);
      if (parsed.isValid) {
        return parsed.toUTC().toISO();
      }
    }

    if (this.isNumericProperty(path) && value !== '' && isFinite(Number(value))) {
      return Number(value);
    }

    return value;
  }

  private isEmptyFieldValueShell(value: any): boolean {
    if (!this.isObject(value) || value.fieldTemplateId === null || value.fieldTemplateId === undefined) {
      return false;
    }

    return !(
      value.stringValue !== null && value.stringValue !== undefined && value.stringValue !== '' ||
      value.intValue !== null && value.intValue !== undefined ||
      value.decimalValue !== null && value.decimalValue !== undefined ||
      value.boolValue === true ||
      value.dateTimeValue !== null && value.dateTimeValue !== undefined ||
      value.selectedOptionId !== null && value.selectedOptionId !== undefined ||
      value.value !== null && value.value !== undefined && value.value !== ''
    );
  }

  private isEmptyPlaceholderShell(value: any, path: Array<string | number>): boolean {
    if (!this.isObject(value) || value.id === null || value.id === undefined) {
      return false;
    }

    // UI-created final-value and leg-summary rows stop having id=0 after their first save.
    // A database identity does not turn an otherwise empty row into a user-entered value.
    const isUiSeededContainer = path[0] === 'jackingFinalValues' || path[0] === 'jackingLegSummary';
    if (value.id !== 0 && !isUiSeededContainer) {
      return false;
    }

    if (Array.isArray(value.legs) && value.legs.some(leg => !this.isEmptyPlaceholderLeg(leg, isUiSeededContainer))) {
      return false;
    }

    const ignored = new Set(['id', 'legs']);
    return Object.keys(value)
      .filter(key => !ignored.has(key))
      .every(key => this.isDefaultPlaceholderValue(value[key]));
  }

  private isEmptyPlaceholderLeg(value: any, allowPersistedId: boolean): boolean {
    if (!this.isObject(value) || value.id === null || value.id === undefined ||
        (value.id !== 0 && !allowPersistedId)) {
      return false;
    }

    return Object.keys(value)
      .filter(key => key !== 'id' && key !== 'legNumber')
      .every(key => this.isDefaultPlaceholderValue(value[key]));
  }

  private isEmptyLiftPumpsShell(value: any, path: Array<string | number>): boolean {
    if (String(path[path.length - 1] || '') !== 'liftPumpsUsed' || !this.isObject(value)) {
      return false;
    }

    const hasSelectedPump = Array.isArray(value.legs) && value.legs.some(leg => leg?.liftPumpUsed === true);
    return value.depthDeployed !== true && !hasSelectedPump;
  }

  private isDefaultPlaceholderValue(value: any): boolean {
    return value === null || value === undefined || value === '' || value === false;
  }

  private isConfirmationPath(path: Array<string | number>): boolean {
    const property = String(path[path.length - 1] || '');
    return /confirmed$/i.test(property);
  }

  private isInitializationOnlyIncomingChange(
    change: JackingMergeIncomingChange,
    editor: JackingFormInstanceEditor
  ): boolean {
    if (change.path[0] !== 'valuesByFieldId') {
      return false;
    }

    const template = this.findFieldTemplate(editor, Number(change.path[1]));
    return !!template?.readOnly;
  }

  private hasStableArrayIdentity(base: any[], local: any[], server: any[], path: Array<string | number>): boolean {
    const all = [...base, ...local, ...server];
    return all.length === 0 || all.every(item => item != null && typeof item === 'object' &&
      this.arrayIdentity(item, path) != null);
  }

  private arrayIdentity(item: any, path: Array<string | number>): string | null {
    if (item === null || item === undefined) {
      return 'primitive:null';
    }
    if (typeof item !== 'object') {
      return `primitive:${typeof item}:${String(this.normalize(item, path))}`;
    }

    const collection = String(path[path.length - 1] || '');
    if (collection === 'fieldValues' && item.fieldTemplateId != null) {
      return `field:${item.fieldTemplateId}`;
    }
    if (collection === 'eventRows') {
      if (item.eventRowTemplateId != null) {
        return `event-template:${item.eventRowTemplateId}`;
      }
      if (item.id != null) {
        return `event:${item.id}`;
      }
    }
    if (collection === 'legs' && item.legNumber != null) {
      return `leg:${item.legNumber}`;
    }
    if (item.id != null) {
      return `id:${item.id}`;
    }
    return null;
  }

  private toIdentityMap(items: any[], path: Array<string | number>): {[identity: string]: any} {
    return items.reduce((map, item) => {
      const identity = this.arrayIdentity(item, path);
      if (identity != null) {
        map[identity] = item;
      }
      return map;
    }, {} as {[identity: string]: any});
  }

  private syncFieldValues(fieldValues: any, valuesByFieldId: {[id: number]: any}): any {
    const values = valuesByFieldId || {};
    if (Array.isArray(fieldValues)) {
      const byId: {[id: string]: any} = {};
      fieldValues.forEach(value => {
        if (value && value.fieldTemplateId != null) {
          byId[String(value.fieldTemplateId)] = value;
        }
      });
      return Object.keys(values).map(id => ({...(cloneDeep(byId[id]) || {}), ...cloneDeep(values[id])}));
    }

    if (this.isObject(fieldValues)) {
      const result = cloneDeep(fieldValues);
      Object.keys(result).forEach(id => {
        const representsField = /^\d+$/.test(id) || result[id]?.fieldTemplateId != null;
        if (representsField && values[id] == null) {
          delete result[id];
        }
      });
      Object.keys(values).forEach(id => {
        result[id] = {...(this.isObject(result[id]) ? result[id] : {}), ...cloneDeep(values[id])};
      });
      return result;
    }

    return cloneDeep(fieldValues);
  }

  private resolveLabel(path: Array<string | number>, editor: JackingFormInstanceEditor): string {
    if (path[0] === 'valuesByFieldId') {
      const template = this.findFieldTemplate(editor, Number(path[1]));
      return template?.label || `Field ${path[1]}`;
    }

    const labels: string[] = [];
    let current: any = editor;
    path.forEach((segment, index) => {
      const property = String(segment);
      if (property === 'jackingEvents') {
        labels.push('Events');
      } else if (property === 'eventRows' && typeof path[index + 1] === 'number') {
        const row = current?.eventRows?.[path[index + 1] as number];
        labels.push(row?.eventName || 'Event');
      } else if (property === 'loadCells') {
        labels.push('Load cells');
      } else if (property === 'jackingFinalValues') {
        labels.push('Final values');
      } else if (property === 'jackingLegSummary') {
        labels.push('Leg summary');
      } else if (property === 'signatures') {
        labels.push('Signatures');
      } else if (property === 'legs' && typeof path[index + 1] === 'number') {
        const leg = current?.legs?.[path[index + 1] as number];
        labels.push(`Leg ${leg?.legNumber || Number(path[index + 1]) + 1}`);
      } else if (index === path.length - 1 || property === 'finalLegValues' || property === 'finalPenetrations' ||
                 property === 'differenceFromExpected' || property === 'finalRpd' || property === 'liftPumpsUsed' ||
                 property === 'finalAirGap' || property === 'position') {
        labels.push(this.humanize(property));
      }
      current = current == null ? null : current[segment as any];
    });

    return labels.filter((label, index) => label && labels.indexOf(label) === index).join(' – ') ||
      this.humanize(String(path[path.length - 1] || 'Value'));
  }

  private formatConflictValue(
    value: any,
    path: Array<string | number>,
    editor: JackingFormInstanceEditor
  ): string {
    if (path[0] === 'valuesByFieldId') {
      const template = this.findFieldTemplate(editor, Number(path[1]));
      return this.formatFieldValue(value, template, editor);
    }

    return this.formatValue(value, editor, path);
  }

  private formatFieldValue(
    stored: any,
    template: JackingFieldTemplateEditor | null,
    editor: JackingFormInstanceEditor
  ): string {
    if (!stored || !template) {
      return this.formatValue(stored, editor, ['valuesByFieldId', template?.id || 'unknown']);
    }

    switch (template.dataTypeId) {
      case 1:
        return this.formatValue(stored.stringValue, editor);
      case 2:
        return this.formatValue(stored.intValue, editor);
      case 3:
        return this.formatValue(stored.decimalValue, editor);
      case 4:
      case 5:
        return this.formatDateTimeValue(stored.dateTimeValue, stored.timeZoneOffsetValue);
      case 6: {
        const selectedId = stored.intValue;
        if (selectedId === null || selectedId === undefined || selectedId === '') {
          return '(empty)';
        }
        const selected = this.getFieldOptions(editor, template)
          .find(option => String(option.id) === String(selectedId));
        return selected?.name || String(selectedId);
      }
      case 7:
        return stored.boolValue === null || stored.boolValue === undefined
          ? '(empty)'
          : (stored.boolValue ? 'Yes' : 'No');
      default:
        return this.formatValue(stored, editor, ['valuesByFieldId', template.id]);
    }
  }

  private formatValue(
    value: any,
    editor: JackingFormInstanceEditor,
    path: Array<string | number> = []
  ): string {
    if (value === null || value === undefined || value === '') {
      return '(empty)';
    }
    if (this.isLuxonDateTime(value)) {
      return value.toFormat('dd/LL/yyyy HH:mm');
    }
    if (this.isSerializedLuxonDateTime(value, path)) {
      return DateTime.fromMillis(Number(value.ts)).toFormat('dd/LL/yyyy HH:mm');
    }
    if (this.isDateValue(value) || this.isPtLuxonDateWrapper(value)) {
      const dateTime = this.isLuxonDateTime(value) ? value : value.dateTime;
      return this.formatDateTimeValue(dateTime, value.offset ?? value.timeOffset);
    }
    if (Array.isArray(value)) {
      const displays = value
        .map((item, index) => this.formatValue(item, editor, path.concat(index)))
        .filter(display => display !== '(empty)');
      return displays.join('; ') || '(empty)';
    }
    if (this.isObject(value)) {
      const valueProperty = ['stringValue', 'decimalValue', 'intValue', 'boolValue', 'dateTimeValue', 'value']
        .find(key => value[key] !== null && value[key] !== undefined && value[key] !== '');
      if (valueProperty) {
        return this.formatValue(value[valueProperty], editor, path.concat(valueProperty));
      }
      return this.formatObjectValue(value, editor, path);
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (typeof value === 'string' && this.isDatePath(path)) {
      const formattedDate = this.formatDateTimeValue(value);
      if (formattedDate !== '(empty)') {
        return formattedDate;
      }
    }
    return String(value);
  }

  private formatDateTimeValue(value: any, offset?: number | null): string {
    if (!value) {
      return '(empty)';
    }

    const rawDateTime = this.isLuxonDateTime(value) ? value : value?.dateTime || value;
    const dateTime = this.isLuxonDateTime(rawDateTime)
      ? rawDateTime
      : (typeof rawDateTime === 'string' ? DateTime.fromISO(rawDateTime) : null);
    if (!this.isLuxonDateTime(dateTime) || !dateTime.isValid) {
      return '(empty)';
    }

    const numericOffset = offset === null || offset === undefined ? null : Number(offset);
    const local = numericOffset === null || !isFinite(numericOffset)
      ? dateTime
      : dateTime.plus({hours: numericOffset});
    const offsetLabel = numericOffset === null || !isFinite(numericOffset)
      ? ''
      : ` (${numericOffset >= 0 ? '+' : ''}${numericOffset})`;
    return `${local.toFormat('dd/LL/yyyy HH:mm')}${offsetLabel}`;
  }

  private formatObjectValue(
    value: any,
    editor: JackingFormInstanceEditor,
    path: Array<string | number>
  ): string {
    const ignoredProperties = new Set([
      'id', 'fieldTemplateId', 'eventRowTemplateId', 'sectionGroupTemplateId', 'displayOrder',
      'eventKey', 'eventName', 'linkTargetType', 'dateParserService', 'isLostInTime', 'multipleOffsets',
      'millisecondsInHour', 'millisecondsInMinute', 'valueOf'
    ]);

    const parts = Object.keys(value)
      .filter(key => !ignoredProperties.has(key) && key.charAt(0) !== '_' &&
        !/^(requires|has|canBe)/.test(key) && typeof value[key] !== 'function')
      .map(key => {
        const display = this.formatValue(value[key], editor, path.concat(key));
        return display === '(empty)' ? null : `${this.humanize(key)}: ${display}`;
      })
      .filter(part => !!part);

    return parts.join('; ') || '(empty)';
  }

  private normalizeDateTime(value: any): string | null {
    if (this.isLuxonDateTime(value)) {
      return value.toUTC().toISO();
    }
    if (typeof value === 'string') {
      const parsed = DateTime.fromISO(value);
      return parsed.isValid ? parsed.toUTC().toISO() : value;
    }
    return null;
  }

  private getFieldOptions(editor: JackingFormInstanceEditor, template: JackingFieldTemplateEditor): any[] {
    if (!template?.optionsKey || !editor?.optionsByKey) {
      return [];
    }

    const exact = editor.optionsByKey[template.optionsKey];
    if (exact) {
      return exact;
    }

    const requestedKey = template.optionsKey.toLowerCase();
    const actualKey = Object.keys(editor.optionsByKey)
      .find(key => key.toLowerCase() === requestedKey);
    return actualKey ? editor.optionsByKey[actualKey] : [];
  }

  private findFieldTemplate(editor: JackingFormInstanceEditor, id: number): JackingFieldTemplateEditor | null {
    for (const section of editor?.template?.sections || []) {
      for (const group of section.groups || []) {
        const field = (group.fields || []).find(candidate => candidate.id === id);
        if (field) {
          return field;
        }
      }
    }
    return null;
  }

  private setAtPath(root: any, path: Array<string | number>, value: any): void {
    if (!path.length) {
      return;
    }
    let parent = root;
    for (let index = 0; index < path.length - 1; index++) {
      parent = parent[path[index] as any];
      if (parent == null) {
        return;
      }
    }
    const key = path[path.length - 1];
    if (value === undefined) {
      if (Array.isArray(parent)) {
        parent.splice(Number(key), 1);
      } else {
        delete parent[key as any];
      }
    } else {
      parent[key as any] = value;
    }
  }

  private uniqueKeys(...values: any[]): string[] {
    const keys: string[] = [];
    values.forEach(value => {
      if (this.isObject(value)) {
        Object.keys(value).forEach(key => {
          if (keys.indexOf(key) < 0) {
            keys.push(key);
          }
        });
      }
    });
    return keys;
  }

  private isNumericProperty(path: Array<string | number>): boolean {
    const property = String(path[path.length - 1] || '');
    return /(^id$|Id$|Offset|Number$|Value$|load$|ext$|penetration$|waterDepth$|jettingPressure$|^r$|^p$|^d$|airGap|northing|easting|heading|distance|depth|pulling)/i.test(property)
      && property !== 'stringValue';
  }

  private isDateValue(value: any): boolean {
    return this.isLuxonDateTime(value) || this.isPtLuxonDateWrapper(value);
  }

  private isPtLuxonDateWrapper(value: any): boolean {
    if (!this.isObject(value)) {
      return false;
    }

    return Object.prototype.hasOwnProperty.call(value, 'dateTime') ||
      Object.prototype.hasOwnProperty.call(value, 'isLostInTime') &&
      (Object.prototype.hasOwnProperty.call(value, 'dateParserService') ||
       Object.prototype.hasOwnProperty.call(value, 'millisecondsInHour'));
  }

  private isSerializedLuxonDateTime(value: any, path: Array<string | number>): boolean {
    return this.isObject(value) && this.isDatePath(path) &&
      value.ts !== null && value.ts !== undefined && isFinite(Number(value.ts));
  }

  private isDatePath(path: Array<string | number>): boolean {
    const property = String(path[path.length - 1] || '');
    return /(date|time)(utc|value)?$/i.test(property) && !/(offset|confirmed|zone)/i.test(property);
  }

  private isLuxonDateTime(value: any): boolean {
    return !!value && typeof value === 'object' && typeof value.toUTC === 'function' &&
      typeof value.toISO === 'function' && typeof value.isValid === 'boolean';
  }

  private isObject(value: any): boolean {
    return !!value && typeof value === 'object' && !Array.isArray(value) && !this.isLuxonDateTime(value);
  }

  private humanize(value: string): string {
    const known: {[key: string]: string} = {
      finalRpd: 'Final RPD', r: 'R', p: 'P', d: 'D', ext: 'Extension', eventDate: 'Date/time',
      timeUtc: 'Date/time', timeOffset: 'Time offset', selectedCells: 'Selected cells', finalAirGap: 'Final air gap',
      finalLegValues: 'Final leg values', finalPenetrations: 'Final penetrations', differenceFromExpected: 'Difference from expected',
      liftPumpsUsed: 'Lift pumps used', position: 'Position'
    };
    if (known[value]) {
      return known[value];
    }
    const spaced = value.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }
}



// WEBPACK FOOTER //
// ./src/services/jacking-v2/jacking-three-way-merge.service.ts